/**
 * PerformanceTab — Filter closed trades by date window and see aggregated stats.
 *
 * Filter basis: 'exit_date' (default) or 'entry_date'.
 * Presets: 1M, 3M, 6M, YTD, 1Y, All.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2, Calendar, Filter, TrendingUp, TrendingDown, BarChart3, Sparkles,
  Activity,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { formatPct, formatMoney } from './utils';
import { SortHeader, useSortable } from './SortableHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';

const todayStr = () => new Date().toISOString().slice(0, 10);
const minusDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const ytdStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const PRESETS = [
  { key: '1M', label: '1M', from: () => minusDays(30) },
  { key: '3M', label: '3M', from: () => minusDays(90) },
  { key: '6M', label: '6M', from: () => minusDays(180) },
  { key: 'YTD', label: 'YTD', from: () => ytdStart() },
  { key: '1Y', label: '1Y', from: () => minusDays(365) },
  { key: 'ALL', label: 'All', from: () => '' },
];

const PerformanceTab = ({ profile = 'largecap' }) => {
  const { authFetch } = useAuth();
  const [fromDate, setFromDate] = useState(minusDays(90));
  const [toDate, setToDate] = useState(todayStr());
  const [basis, setBasis] = useState('exit_date');
  const [activePreset, setActivePreset] = useState('3M');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      params.set('basis', basis);
      params.set('profile', profile);
      const res = await authFetch(`${API}/api/alphascout/positions/performance?${params}`);
      const d = await res.json();
      setData(d);
    } catch (e) {
      console.error('[PerformanceTab] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [authFetch, fromDate, toDate, basis, profile]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => {
    setActivePreset(p.key);
    setFromDate(p.from());
    setToDate(todayStr());
  };

  const clearPreset = () => setActivePreset(null);

  const trades = data?.trades || [];
  const stats = data?.stats || {};
  const sectors = data?.sectors || [];
  const curve = data?.equity_curve || [];

  // Sortable trade table
  const sortAccessors = useMemo(() => ({
    entry_date: (r) => (r.entry_date ? new Date(r.entry_date).getTime() : 0),
    exit_date: (r) => (r.exit_date ? new Date(r.exit_date).getTime() : 0),
  }), []);
  const { sorted, field, dir, toggle } = useSortable(trades, {
    initialField: 'exit_date',
    initialDir: 'desc',
    accessors: sortAccessors,
  });

  return (
    <div className="space-y-4" data-testid="performance-tab">
      {/* Filter bar */}
      <div className="bg-surface border border-border p-3 space-y-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Filter className="w-3.5 h-3.5" />
          Filter by date window
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => applyPreset(p)}
              className={`px-3 py-1 text-xs font-mono font-semibold border transition-colors ${
                activePreset === p.key
                  ? 'bg-accent/10 border-accent text-accent'
                  : 'bg-surface-highlight border-border text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`preset-${p.key.toLowerCase()}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom range + basis */}
        <div className="flex flex-wrap items-end gap-3">
          <DateInput
            label="From"
            value={fromDate}
            onChange={(v) => { setFromDate(v); clearPreset(); }}
            testId="from-date"
          />
          <DateInput
            label="To"
            value={toDate}
            onChange={(v) => { setToDate(v); clearPreset(); }}
            testId="to-date"
          />
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Filter by</label>
            <select
              value={basis}
              onChange={(e) => setBasis(e.target.value)}
              className="bg-surface-highlight border border-border text-xs px-2 py-1.5 text-foreground focus:outline-none focus:border-accent"
              data-testid="basis-select"
            >
              <option value="exit_date">Exit Date</option>
              <option value="entry_date">Entry Date</option>
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
            <Calendar className="w-3 h-3" />
            {fromDate || 'earliest'} → {toDate || 'latest'}
          </div>
        </div>
      </div>

      {loading && !data && (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <StatCard label="Trades" value={stats.count || 0} icon={BarChart3} />
            <StatCard
              label="Win Rate"
              value={`${stats.win_rate_pct || 0}%`}
              subvalue={`${stats.wins || 0}W / ${stats.losses || 0}L`}
              positive={stats.win_rate_pct > 50}
            />
            <StatCard
              label="Avg Return"
              value={formatPct(stats.avg_return_pct)}
              positive={stats.avg_return_pct > 0}
            />
            <StatCard
              label="Cumulative"
              value={formatPct(stats.sum_return_pct)}
              subvalue="(sum of returns)"
              positive={stats.sum_return_pct > 0}
            />
            <StatCard label="Avg Hold" value={`${stats.avg_hold_days || 0}d`} />
            <StatCard
              label="Best"
              value={stats.best?.symbol || '–'}
              subvalue={stats.best?.symbol ? formatPct(stats.best.return_pct) : null}
              positive
              icon={TrendingUp}
            />
            <StatCard
              label="Worst"
              value={stats.worst?.symbol || '–'}
              subvalue={stats.worst?.symbol ? formatPct(stats.worst.return_pct) : null}
              positive={false}
              icon={TrendingDown}
            />
          </div>

          {/* Equity curve */}
          {curve.length > 0 ? (
            <EquityCurve
              curve={curve}
              maxDrawdown={stats.max_drawdown_pct || 0}
              finalReturn={stats.sum_return_pct || 0}
            />
          ) : (
            <div
              className="bg-surface border border-border border-dashed p-8 text-center"
              data-testid="perf-equity-curve-empty"
            >
              <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
              <p className="text-sm text-foreground font-semibold mb-1">
                Cumulative Return Curve
              </p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                No closed trades in this window yet. Once a rebalance exits any positions,
                this area will show an interactive area chart of your P&L curve with running
                drawdown tracking. Try a wider date range (e.g. <span className="font-mono">All</span>) to check older history.
              </p>
            </div>
          )}

          {/* Sector breakdown */}
          {sectors.length > 0 && (
            <div className="bg-surface border border-border overflow-hidden" data-testid="perf-sector-breakdown">
              <div className="px-3 py-2 border-b border-border bg-surface-highlight text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> Sector Breakdown
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-highlight border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">Sector</th>
                      <th className="px-3 py-2 text-right">Trades</th>
                      <th className="px-3 py-2 text-right">Win Rate</th>
                      <th className="px-3 py-2 text-right">Avg Return</th>
                      <th className="px-3 py-2 text-right">Sum Return</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectors.map((s) => (
                      <tr key={s.sector} className="border-b border-border/40 hover:bg-surface-highlight">
                        <td className="px-3 py-2 text-xs text-foreground">{s.sector}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{s.count}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{s.win_rate_pct}%</td>
                        <td
                          className={`px-3 py-2 text-right font-mono text-xs font-bold ${
                            s.avg_return_pct > 0 ? 'text-emerald-400' : s.avg_return_pct < 0 ? 'text-red-400' : ''
                          }`}
                        >
                          {formatPct(s.avg_return_pct)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right font-mono text-xs font-bold ${
                            s.sum_return_pct > 0 ? 'text-emerald-400' : s.sum_return_pct < 0 ? 'text-red-400' : ''
                          }`}
                        >
                          {formatPct(s.sum_return_pct)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Trades table */}
          <div className="bg-surface border border-border overflow-hidden">
            <div className="px-3 py-2 border-b border-border bg-surface-highlight text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Trades in window · {trades.length}
            </div>
            {trades.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                No closed trades in this window. Try a wider range.
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-highlight border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground sticky top-0">
                    <tr>
                      <SortHeader label="Symbol" sortKey="symbol" active={field} dir={dir} onToggle={toggle} />
                      <SortHeader label="Sector" sortKey="sector" active={field} dir={dir} onToggle={toggle} />
                      <SortHeader label="Entry Date" sortKey="entry_date" active={field} dir={dir} onToggle={toggle} />
                      <SortHeader label="Entry" sortKey="entry_price" active={field} dir={dir} onToggle={toggle} align="right" />
                      <SortHeader label="Exit Date" sortKey="exit_date" active={field} dir={dir} onToggle={toggle} />
                      <SortHeader label="Exit" sortKey="exit_price" active={field} dir={dir} onToggle={toggle} align="right" />
                      <SortHeader label="Return" sortKey="realized_return_pct" active={field} dir={dir} onToggle={toggle} align="right" />
                      <SortHeader label="Days" sortKey="days_held" active={field} dir={dir} onToggle={toggle} align="right" />
                      <SortHeader label="Reason" sortKey="exit_reason" active={field} dir={dir} onToggle={toggle} />
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((t, i) => {
                      const ret = t.realized_return_pct || 0;
                      const isWin = ret > 0;
                      return (
                        <tr key={`${t.symbol}-${i}`} className="border-b border-border/40 hover:bg-surface-highlight">
                          <td className="px-3 py-2 font-bold text-foreground">{t.symbol}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[140px]">{t.sector}</td>
                          <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{(t.entry_date || '').slice(0, 10)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">₹{formatMoney(t.entry_price)}</td>
                          <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{(t.exit_date || '').slice(0, 10)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">₹{formatMoney(t.exit_price)}</td>
                          <td
                            className={`px-3 py-2 text-right font-mono text-xs font-bold ${
                              isWin ? 'text-emerald-400' : ret < 0 ? 'text-red-400' : ''
                            }`}
                          >
                            <div className="flex items-center justify-end gap-1">
                              {isWin && <TrendingUp className="w-3 h-3" />}
                              {ret < 0 && <TrendingDown className="w-3 h-3" />}
                              {formatPct(ret)}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-muted-foreground">{t.days_held}d</td>
                          <td className="px-3 py-2 text-[10px] text-muted-foreground">{(t.exit_reason || '').replace(/_/g, ' ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const EquityCurve = ({ curve, maxDrawdown, finalReturn }) => {
  const positive = finalReturn >= 0;
  const stroke = positive ? '#34d399' : '#f87171';
  const fillId = positive ? 'perf-area-pos' : 'perf-area-neg';

  // Count unique dates for X-axis tick strategy (show all if few)
  const xTickInterval = curve.length <= 12 ? 0 : Math.ceil(curve.length / 8);

  return (
    <div className="bg-surface border border-border p-4" data-testid="perf-equity-curve">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Activity className="w-3.5 h-3.5" />
          Cumulative Return Curve
        </div>
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
          <span>
            Final:{' '}
            <span className={`font-mono font-bold ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
              {formatPct(finalReturn)}
            </span>
          </span>
          <span>
            Max DD:{' '}
            <span className="font-mono font-bold text-red-400">-{maxDrawdown}%</span>
          </span>
          <span>{curve.length} trades</span>
        </div>
      </div>

      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={curve} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="perf-area-pos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#34d399" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="perf-area-neg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f87171" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#f87171" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#6b7280', fontSize: 10 }}
              stroke="#30363d"
              interval={xTickInterval}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 10 }}
              stroke="#30363d"
              tickFormatter={(v) => `${v}%`}
              width={48}
            />
            <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 4" />
            <Tooltip content={<EquityTooltip />} />
            <Area
              type="monotone"
              dataKey="cum_return_pct"
              name="Cumulative"
              stroke={stroke}
              strokeWidth={2}
              fill={`url(#${fillId})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
        Each point is one closed trade, plotted at its exit date. The line is the running sum of
        realized returns — think of it as your P&L curve if each trade were equally weighted.
      </div>
    </div>
  );
};

const EquityTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-surface border border-border p-2 shadow-lg text-xs">
      <div className="font-mono text-muted-foreground mb-1">{label}</div>
      {p.symbol && (
        <div className="text-foreground font-bold mb-0.5">{p.symbol}</div>
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">Trade:</span>
        <span
          className={`font-mono font-bold ${
            p.trade_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
          }`}
        >
          {formatPct(p.trade_return_pct)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">Cumulative:</span>
        <span
          className={`font-mono font-bold ${
            p.cum_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'
          }`}
        >
          {formatPct(p.cum_return_pct)}
        </span>
      </div>
      {p.drawdown_pct > 0 && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Drawdown:</span>
          <span className="font-mono text-red-400">-{p.drawdown_pct}%</span>
        </div>
      )}
    </div>
  );
};

const DateInput = ({ label, value, onChange, testId }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-surface-highlight border border-border text-xs px-2 py-1.5 text-foreground focus:outline-none focus:border-accent font-mono"
      data-testid={testId}
    />
  </div>
);

const StatCard = ({ label, value, subvalue, positive, icon: Icon }) => (
  <div className="bg-surface border border-border p-3">
    <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </div>
    <div
      className={`text-lg font-bold font-mono ${
        positive === true ? 'text-emerald-400' : positive === false ? 'text-red-400' : 'text-foreground'
      }`}
    >
      {value}
    </div>
    {subvalue && <div className="text-[10px] text-muted-foreground mt-0.5">{subvalue}</div>}
  </div>
);

export default PerformanceTab;
